import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  OpenInNew as OpenIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { colors, customShadows, borderRadius, borderRadiusPx } from "../../../../theme";
import { CommonButton, CommonChip } from "../../ui";

/**
 * WalletTreasuryCard - Tax Management Wallet, balances, liability status, and reporting actions.
 */
const WalletTreasuryCard = ({
  title = "Tax Management Wallet",
  walletLabel = "Online - PKL081",
  walletAddress = "",
  balance = "87,524.80",
  collected = "87,524.80",
  remitted = "0.00",
  outstandingLiability = "87,524.80",
  statusBadge = "AWAITING LIABILITY PAYMENT",
  statusColor = colors.dashboard.status.pendingText || "#b45309",
  statusBg = colors.dashboard.status.pendingBg || "#fef3c7",
  nextReview = "Quarterly",
  currencyPrefix = "K ",
  currencySuffix = "",
  onViewTransactions,
  onPrimaryAction,
  primaryActionLabel = "Generate Tax Report",
  secondaryActionLabel = "View Transactions",
  sx = {},
}) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatAmount = (val) => {
    if (val === undefined || val === null || val === "") return "";
    const str = String(val);
    if (
      str.startsWith("K ") ||
      str.startsWith("K") ||
      str.startsWith("$") ||
      str.includes("POL") ||
      str.includes("BET")
    ) {
      return str;
    }
    return `${currencyPrefix}${str}${currencySuffix}`;
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: borderRadiusPx.card,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardSubtle,
        backgroundColor: colors.dashboard.cardBg,
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Header with Title & Online Status */}
        {title && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              color: colors.dashboard.title,
              mb: 0.5,
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>
        )}

        {(walletLabel || walletAddress) && (
          <Stack direction="row" spacing={0.75} alignItems="center" mb={2}>
            {walletLabel && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.82rem", color: colors.slate[700] }}
              >
                {walletLabel}
              </Typography>
            )}
            {walletAddress && (
              <Tooltip title={copied ? "Copied address!" : "Copy wallet address"}>
                <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
                  <CopyIcon sx={{ fontSize: "0.85rem", color: colors.slate[500] }} />
                </IconButton>
              </Tooltip>
            )}
            <OpenIcon sx={{ fontSize: "0.9rem", color: colors.slate[500], cursor: "pointer" }} />
          </Stack>
        )}

        {/* Balances List */}
        <Stack spacing={1.1} sx={{ flex: 1 }}>
          {balance !== "" && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.82rem" }}>
                Wallet Balance
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem", color: colors.slate[900] }}>
                {formatAmount(balance)}
              </Typography>
            </Stack>
          )}

          {collected !== "" && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.82rem" }}>
                Collected This Quarter
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem", color: colors.slate[900] }}>
                {formatAmount(collected)}
              </Typography>
            </Stack>
          )}

          {remitted !== "" && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.82rem" }}>
                Remitted This Quarter
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem", color: colors.slate[900] }}>
                {formatAmount(remitted)}
              </Typography>
            </Stack>
          )}

          {outstandingLiability !== "" && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500, fontSize: "0.82rem" }}>
                Outstanding Liability
              </Typography>
              <Typography variant="body2" sx={{ color: colors.error.main, fontWeight: 700, fontSize: "0.82rem" }}>
                {formatAmount(outstandingLiability)}
              </Typography>
            </Stack>
          )}

          {(statusBadge || nextReview) && (
            <Divider sx={{ my: 0.75, borderColor: colors.dashboard.divider }} />
          )}

          {/* Status Chip */}
          {statusBadge && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={0.5}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.slate[500],
                  fontSize: "0.82rem",
                  flexShrink: 0,
                }}
              >
                Status
              </Typography>
              <CommonChip
                status={statusBadge}
                backgroundColor={statusBg}
                color={statusColor}
                sx={{
                  height: "auto",
                  minHeight: 22,
                  py: 0.35,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    px: 1,
                    whiteSpace: "normal",
                    textAlign: "right",
                    lineHeight: 1.25,
                  },
                }}
              />
            </Stack>
          )}

          {/* Next Review */}
          {nextReview && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.82rem" }}>
                Next Review Frequency
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem", color: colors.slate[900] }}>
                {nextReview}
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Action Buttons */}
        {(secondaryActionLabel || primaryActionLabel) && (
          <Stack direction="row" spacing={1.25} sx={{ mt: 2.5 }}>
            {secondaryActionLabel && (
              <CommonButton
                variant="outlined"
                fullWidth
                size="small"
                onClick={onViewTransactions}
              >
                {secondaryActionLabel}
              </CommonButton>
            )}

            {primaryActionLabel && (
              <CommonButton
                variant="contained"
                fullWidth
                size="small"
                onClick={onPrimaryAction}
              >
                {primaryActionLabel}
              </CommonButton>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

WalletTreasuryCard.propTypes = {
  title: PropTypes.string,
  walletLabel: PropTypes.string,
  walletAddress: PropTypes.string,
  balance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  collected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  remitted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  outstandingLiability: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  statusBadge: PropTypes.string,
  statusColor: PropTypes.string,
  statusBg: PropTypes.string,
  nextReview: PropTypes.string,
  currencyPrefix: PropTypes.string,
  currencySuffix: PropTypes.string,
  onViewTransactions: PropTypes.func,
  onPrimaryAction: PropTypes.func,
  primaryActionLabel: PropTypes.string,
  secondaryActionLabel: PropTypes.string,
  sx: PropTypes.object,
};

export default WalletTreasuryCard;
